import { supabase } from '../lib/supabase'
import type {
  EstoqueCategoria,
  EstoqueCategoriaInput,
  EstoqueFornecedor,
  EstoqueFornecedorInput,
  EstoqueItem,
  EstoqueItemFoto,
  EstoqueItemInput,
  EstoqueLocal,
  EstoqueLocalInput,
  EstoqueMovimentacao,
  EstoqueMovimentacaoFilters,
  EstoqueMovementType,
  EstoqueSaldo,
  EstoqueSaldoFilters,
  PaymentMethod,
  PaymentMethodInput,
  SoftDeleteParams,
} from '../types/estoque'

const BUCKET = 'marketing-stock-items'

function softDeletePayload(params?: SoftDeleteParams) {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: params?.deleted_by ?? null,
    deleted_reason: params?.deleted_reason ?? null,
  }
}

export const estoqueService = {
  // ============================================
  // CATEGORIES
  // ============================================
  async listCategorias(includeInactive = true): Promise<EstoqueCategoria[]> {
    let query = supabase
      .from('marketing_stock_categories')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as EstoqueCategoria[]
  },

  async createCategoria(input: EstoqueCategoriaInput): Promise<EstoqueCategoria> {
    const { data, error } = await supabase
      .from('marketing_stock_categories')
      .insert({
        name: input.name.trim(),
        description: input.description?.trim() || null,
        is_active: input.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as EstoqueCategoria
  },

  async updateCategoria(id: string, input: Partial<EstoqueCategoriaInput>): Promise<EstoqueCategoria> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) payload.name = input.name.trim()
    if (input.description !== undefined) payload.description = input.description?.trim() || null
    if (input.is_active !== undefined) payload.is_active = input.is_active

    const { data, error } = await supabase
      .from('marketing_stock_categories')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throw error
    return data as EstoqueCategoria
  },

  async softDeleteCategoria(id: string, params?: SoftDeleteParams): Promise<'deleted' | 'inactivated'> {
    const { count, error: countError } = await supabase
      .from('marketing_stock_items')
      .select('id', { count: 'exact', head: true })
      .eq('category_id', id)
      .is('deleted_at', null)

    if (countError) throw countError

    if ((count ?? 0) > 0) {
      await this.updateCategoria(id, { is_active: false })
      return 'inactivated'
    }

    const { error } = await supabase
      .from('marketing_stock_categories')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
    return 'deleted'
  },

  // ============================================
  // LOCATIONS
  // ============================================
  async listLocais(includeInactive = true): Promise<EstoqueLocal[]> {
    let query = supabase
      .from('marketing_stock_locations')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as EstoqueLocal[]
  },

  async createLocal(input: EstoqueLocalInput): Promise<EstoqueLocal> {
    const { data, error } = await supabase
      .from('marketing_stock_locations')
      .insert({
        name: input.name.trim(),
        description: input.description?.trim() || null,
        is_active: input.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as EstoqueLocal
  },

  async updateLocal(id: string, input: Partial<EstoqueLocalInput>): Promise<EstoqueLocal> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) payload.name = input.name.trim()
    if (input.description !== undefined) payload.description = input.description?.trim() || null
    if (input.is_active !== undefined) payload.is_active = input.is_active

    const { data, error } = await supabase
      .from('marketing_stock_locations')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throw error
    return data as EstoqueLocal
  },

  async softDeleteLocal(id: string, params?: SoftDeleteParams): Promise<'deleted' | 'inactivated'> {
    const { count, error: countError } = await supabase
      .from('marketing_stock_movements')
      .select('id', { count: 'exact', head: true })
      .eq('location_id', id)
      .is('deleted_at', null)

    if (countError) throw countError

    if ((count ?? 0) > 0) {
      await this.updateLocal(id, { is_active: false })
      return 'inactivated'
    }

    const { error } = await supabase
      .from('marketing_stock_locations')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
    return 'deleted'
  },

  // ============================================
  // SUPPLIERS
  // ============================================
  async listFornecedores(includeInactive = true): Promise<EstoqueFornecedor[]> {
    let query = supabase
      .from('suppliers')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as EstoqueFornecedor[]
  },

  async createFornecedor(input: EstoqueFornecedorInput): Promise<EstoqueFornecedor> {
    const { data, error } = await supabase
      .from('suppliers')
      .insert({
        name: input.name.trim(),
        address: input.address?.trim() || null,
        city: input.city?.trim() || null,
        state: input.state?.trim().toUpperCase() || null,
        contact_name: input.contact_name?.trim() || null,
        phone: input.phone?.trim() || null,
        email: input.email?.trim() || null,
        notes: input.notes?.trim() || null,
        is_active: input.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as EstoqueFornecedor
  },

  async updateFornecedor(id: string, input: Partial<EstoqueFornecedorInput>): Promise<EstoqueFornecedor> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) payload.name = input.name.trim()
    if (input.address !== undefined) payload.address = input.address?.trim() || null
    if (input.city !== undefined) payload.city = input.city?.trim() || null
    if (input.state !== undefined) payload.state = input.state?.trim().toUpperCase() || null
    if (input.contact_name !== undefined) payload.contact_name = input.contact_name?.trim() || null
    if (input.phone !== undefined) payload.phone = input.phone?.trim() || null
    if (input.email !== undefined) payload.email = input.email?.trim() || null
    if (input.notes !== undefined) payload.notes = input.notes?.trim() || null
    if (input.is_active !== undefined) payload.is_active = input.is_active

    const { data, error } = await supabase
      .from('suppliers')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throw error
    return data as EstoqueFornecedor
  },

  async softDeleteFornecedor(id: string, params?: SoftDeleteParams): Promise<void> {
    const { error } = await supabase
      .from('suppliers')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  // ============================================
  // PAYMENT METHODS
  // ============================================
  async listFormasPagamento(includeInactive = true): Promise<PaymentMethod[]> {
    let query = supabase
      .from('payment_methods')
      .select('*')
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as PaymentMethod[]
  },

  async createFormaPagamento(input: PaymentMethodInput): Promise<PaymentMethod> {
    const { data, error } = await supabase
      .from('payment_methods')
      .insert({
        name: input.name.trim(),
        settlement: input.settlement,
        is_active: input.is_active ?? true,
      })
      .select('*')
      .single()

    if (error) throw error
    return data as PaymentMethod
  },

  async updateFormaPagamento(id: string, input: Partial<PaymentMethodInput>): Promise<PaymentMethod> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) payload.name = input.name.trim()
    if (input.settlement !== undefined) payload.settlement = input.settlement
    if (input.is_active !== undefined) payload.is_active = input.is_active

    const { data, error } = await supabase
      .from('payment_methods')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error) throw error
    return data as PaymentMethod
  },

  async softDeleteFormaPagamento(id: string, params?: SoftDeleteParams): Promise<void> {
    const { error } = await supabase
      .from('payment_methods')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  // ============================================
  // ITEMS
  // ============================================
  async listItens(
    includeInactive = true,
    options?: { itemType?: EstoqueItem['item_type']; availableForRequest?: boolean }
  ): Promise<EstoqueItem[]> {
    let query = supabase
      .from('marketing_stock_items')
      .select(`
        *,
        category:marketing_stock_categories(*),
        photos:marketing_stock_item_photos(*)
      `)
      .is('deleted_at', null)
      .order('name', { ascending: true })

    if (!includeInactive) {
      query = query.eq('is_active', true)
    }
    if (options?.itemType) {
      query = query.eq('item_type', options.itemType)
    }
    if (options?.availableForRequest === true) {
      query = query.eq('available_for_request', true)
    }

    const { data, error } = await query
    if (error) throw error

    return ((data || []) as EstoqueItem[]).map((item) => ({
      ...item,
      photos: (item.photos || [])
        .filter((foto) => !foto.deleted_at)
        .sort((a, b) => a.sort_order - b.sort_order),
    }))
  },

  async getItem(id: string): Promise<EstoqueItem | null> {
    const { data, error } = await supabase
      .from('marketing_stock_items')
      .select(`
        *,
        category:marketing_stock_categories(*),
        photos:marketing_stock_item_photos(*)
      `)
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    if (!data) return null

    const item = data as EstoqueItem
    return {
      ...item,
      photos: (item.photos || [])
        .filter((foto) => !foto.deleted_at)
        .sort((a, b) => a.sort_order - b.sort_order),
    }
  },

  async createItem(input: EstoqueItemInput): Promise<EstoqueItem> {
    const { data, error } = await supabase
      .from('marketing_stock_items')
      .insert({
        name: input.name.trim(),
        item_type: input.item_type,
        category_id: input.category_id,
        unit: input.unit?.trim() || 'un',
        min_stock: input.min_stock ?? 0,
        description: input.description?.trim() || null,
        is_active: input.is_active ?? true,
        available_for_request: input.available_for_request ?? false,
      })
      .select(`
        *,
        category:marketing_stock_categories(*),
        photos:marketing_stock_item_photos(*)
      `)
      .single()

    if (error) throw error
    return data as EstoqueItem
  },

  async updateItem(id: string, input: Partial<EstoqueItemInput>): Promise<EstoqueItem> {
    const payload: Record<string, unknown> = {}
    if (input.name !== undefined) payload.name = input.name.trim()
    if (input.item_type !== undefined) payload.item_type = input.item_type
    if (input.category_id !== undefined) payload.category_id = input.category_id
    if (input.unit !== undefined) payload.unit = input.unit.trim() || 'un'
    if (input.min_stock !== undefined) payload.min_stock = input.min_stock
    if (input.description !== undefined) payload.description = input.description?.trim() || null
    if (input.is_active !== undefined) payload.is_active = input.is_active
    if (input.available_for_request !== undefined) {
      payload.available_for_request = input.available_for_request
    }

    const { data, error } = await supabase
      .from('marketing_stock_items')
      .update(payload)
      .eq('id', id)
      .is('deleted_at', null)
      .select(`
        *,
        category:marketing_stock_categories(*),
        photos:marketing_stock_item_photos(*)
      `)
      .single()

    if (error) throw error
    return data as EstoqueItem
  },

  async softDeleteItem(id: string, params?: SoftDeleteParams): Promise<'deleted' | 'inactivated'> {
    const { count, error: countError } = await supabase
      .from('marketing_stock_movements')
      .select('id', { count: 'exact', head: true })
      .eq('item_id', id)
      .is('deleted_at', null)

    if (countError) throw countError

    if ((count ?? 0) > 0) {
      await this.updateItem(id, { is_active: false })
      return 'inactivated'
    }

    const { error } = await supabase
      .from('marketing_stock_items')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
    return 'deleted'
  },

  // ============================================
  // PHOTOS
  // ============================================
  async listItemFotos(itemId: string): Promise<EstoqueItemFoto[]> {
    const { data, error } = await supabase
      .from('marketing_stock_item_photos')
      .select('*')
      .eq('item_id', itemId)
      .is('deleted_at', null)
      .order('sort_order', { ascending: true })

    if (error) throw error
    return (data || []) as EstoqueItemFoto[]
  },

  async uploadItemFoto(file: File, itemId: string, uploadedBy?: string | null): Promise<EstoqueItemFoto> {
    const timestamp = Date.now()
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
    const filePath = `${itemId}/${timestamp}_${sanitizedFileName}`

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      })

    if (uploadError) {
      throw new Error(`Falha ao enviar imagem: ${uploadError.message}`)
    }

    const existing = await this.listItemFotos(itemId)
    const sortOrder = existing.length

    const { data, error } = await supabase
      .from('marketing_stock_item_photos')
      .insert({
        item_id: itemId,
        file_path: filePath,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || null,
        sort_order: sortOrder,
        uploaded_by: uploadedBy || null,
      })
      .select('*')
      .single()

    if (error) {
      await supabase.storage.from(BUCKET).remove([filePath])
      throw error
    }

    return data as EstoqueItemFoto
  },

  async getItemFotoUrl(filePath: string, expiresInSeconds = 3600): Promise<string | null> {
    if (!filePath) return null

    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(filePath, expiresInSeconds)

    if (error) {
      console.error('Erro ao gerar URL assinada da imagem:', error.message)
      return null
    }

    return data?.signedUrl ?? null
  },

  async softDeleteItemFoto(id: string, params?: SoftDeleteParams): Promise<void> {
    const { data: foto, error: fetchError } = await supabase
      .from('marketing_stock_item_photos')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (fetchError) throw fetchError
    if (!foto) return

    const { error } = await supabase
      .from('marketing_stock_item_photos')
      .update(softDeletePayload(params))
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error

    try {
      await supabase.storage.from(BUCKET).remove([foto.file_path])
    } catch {
      // Mantém o soft delete mesmo se o storage falhar
    }
  },

  // ============================================
  // BALANCES
  // ============================================
  async listSaldos(filters?: EstoqueSaldoFilters): Promise<EstoqueSaldo[]> {
    let query = supabase
      .from('marketing_stock_balances')
      .select(`
        *,
        item:marketing_stock_items(*, category:marketing_stock_categories(*)),
        location:marketing_stock_locations(*)
      `)
      .is('deleted_at', null)
      .order('updated_at', { ascending: false })

    if (filters?.locationId) {
      query = query.eq('location_id', filters.locationId)
    }

    const { data, error } = await query
    if (error) throw error

    let saldos = (data || []) as EstoqueSaldo[]

    saldos = saldos.filter((saldo) => {
      const item = saldo.item
      if (!item || item.deleted_at) return false
      if (filters?.itemType && item.item_type !== filters.itemType) return false
      if (filters?.categoryId && item.category_id !== filters.categoryId) return false
      if (filters?.search) {
        const term = filters.search.toLowerCase()
        const nameMatch = item.name.toLowerCase().includes(term)
        const codeMatch = String(item.code ?? '').includes(term)
        if (!nameMatch && !codeMatch) return false
      }
      return true
    })

    const totalsByItem = new Map<string, number>()
    const allItemIds = [...new Set(saldos.map((s) => s.item_id))]
    if (allItemIds.length > 0) {
      const { data: allSaldos, error: allError } = await supabase
        .from('marketing_stock_balances')
        .select('item_id, quantity')
        .in('item_id', allItemIds)
        .is('deleted_at', null)

      if (allError) throw allError

      for (const row of allSaldos || []) {
        totalsByItem.set(row.item_id, (totalsByItem.get(row.item_id) || 0) + Number(row.quantity || 0))
      }
    }

    return saldos.map((saldo) => {
      const total = totalsByItem.get(saldo.item_id) || 0
      const minimo = Number(saldo.item?.min_stock || 0)
      return {
        ...saldo,
        total_item_quantity: total,
        below_minimum: minimo > 0 && total < minimo,
      }
    })
  },

  // ============================================
  // MOVEMENTS
  // ============================================
  async listMovimentacoes(filters?: EstoqueMovimentacaoFilters): Promise<EstoqueMovimentacao[]> {
    let query = supabase
      .from('marketing_stock_movements')
      .select(`
        *,
        item:marketing_stock_items(id, name, item_type),
        location:marketing_stock_locations!marketing_stock_movements_location_id_fkey(id, name),
        related_location:marketing_stock_locations!marketing_stock_movements_related_location_id_fkey(id, name),
        created_by_user:profiles!marketing_stock_movements_created_by_fkey(id, name)
      `)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(500)

    if (filters?.itemId) {
      query = query.eq('item_id', filters.itemId)
    }
    if (filters?.locationId) {
      query = query.eq('location_id', filters.locationId)
    }
    if (filters?.movementType && filters.movementType !== 'all') {
      query = query.eq('movement_type', filters.movementType)
    }
    if (filters?.dateFrom) {
      query = query.gte('created_at', filters.dateFrom)
    }
    if (filters?.dateTo) {
      const end = filters.dateTo.includes('T') ? filters.dateTo : `${filters.dateTo}T23:59:59`
      query = query.lte('created_at', end)
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as EstoqueMovimentacao[]
  },

  async registerMovimentacao(params: {
    item_id: string
    location_id: string
    movement_type: Exclude<EstoqueMovementType, 'transfer'>
    quantity: number
    description: string
  }): Promise<EstoqueMovimentacao> {
    const { data, error } = await supabase.rpc('marketing_stock_register_movement', {
      p_item_id: params.item_id,
      p_location_id: params.location_id,
      p_movement_type: params.movement_type,
      p_quantity: params.quantity,
      p_description: params.description.trim(),
    })

    if (error) throw error
    return data as EstoqueMovimentacao
  },

  async transferMovimentacao(params: {
    item_id: string
    from_location_id: string
    to_location_id: string
    quantity: number
    description: string
  }): Promise<EstoqueMovimentacao[]> {
    const { data, error } = await supabase.rpc('marketing_stock_transfer', {
      p_item_id: params.item_id,
      p_from_location_id: params.from_location_id,
      p_to_location_id: params.to_location_id,
      p_quantity: params.quantity,
      p_description: params.description.trim(),
    })

    if (error) throw error
    return (data || []) as EstoqueMovimentacao[]
  },
}
